import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../../api';


export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [userMap, setUserMap] = useState({});
  const [myOnly, setMyOnly] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');

  const fetchTasks = useCallback(() => {
    const params = new URLSearchParams();
    if (statusFilter === 'active') params.set('status', 'active');
    else if (statusFilter === 'done') params.set('status', 'Done');
    if (myOnly && currentUserId) params.set('assignedTo', currentUserId);
    if (searchTerm) params.set('q', searchTerm);
    const qs = params.toString();
    api(`/tasks${qs ? '?' + qs : ''}`).then(r => r.json()).then(setTasks).catch(() => {});
  }, [statusFilter, myOnly, currentUserId, searchTerm]);

  useEffect(() => {
    api('/tenant-users').then(r => r.json()).then(list => {
      const map = {};
      list.forEach(u => { map[u.userId] = `${u.firstName} ${u.lastName}`; });
      setUserMap(map);
    }).catch(() => {});
    api('/me').then(r => r.ok ? r.json() : null).then(u => { if (u) setCurrentUserId(u.userId); }).catch(() => {});
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const total = tasks.length;
  const todo = tasks.filter(t => t.status === 'To Do').length;
  const inProgress = tasks.filter(t => t.status === 'In Progress').length;
  const done = tasks.filter(t => t.status === 'Done').length;

  const stats = [
    { title: 'Total Tasks', value: total },
    { title: 'To Do', value: todo },
    { title: 'In Progress', value: inProgress },
    { title: 'Done', value: done },
  ];

  return (
    <Layout>
      <div className="hidden md:grid grid-cols-4 gap-6 mb-6">
        {stats.map((stat) => (
          <div key={stat.title} className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-500 mb-1">{stat.title}</p>
            <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 md:p-6 border-b">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
            <Link to="/tasks/new" className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700">
              Add Task
            </Link>
          </div>
          <div className="flex gap-3 mt-3">
            <input
              type="text"
              placeholder="Search by title or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-600"
            />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-orange-600">
              <option value="active">Active</option>
              <option value="done">Completed</option>
              <option value="all">All</option>
            </select>
            <button onClick={() => setMyOnly(!myOnly)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${myOnly ? 'bg-orange-600 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
              My Tasks
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Priority</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Assigned To</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tasks.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                    <Link to={`/tasks/${t.id}`} className="text-orange-600 hover:text-orange-800">{t.title}</Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      t.priority === 'High' ? 'bg-red-100 text-red-800' :
                      t.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {t.priority || '—'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600 hidden md:table-cell">{t.assignedTo ? (userMap[t.assignedTo] || t.assignedTo) : '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{t.dueDate || '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      t.status === 'Done' ? 'bg-green-100 text-green-800' :
                      t.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {t.status || '—'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {tasks.length === 0 && (
            <div className="text-center py-12 text-gray-500">No tasks found.</div>
          )}
        </div>
      </div>
    </Layout>
  );
}
